import { useQuery, useMutation, useQueryClient } from "react-query"
import qs from "query-string"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"

/** ------------------- **/
/*  Internal Tools
/** ------------------- **/

export function useHighPriorityCompanies() {
  const cacheKey = ["highPriorityCompanies"]
  const query = () => apiRequest(`companies/list_purchased_companies`)
  return useQuery(cacheKey, query)
}

export function useDataQuality() {
  const cacheKey = ["dataQuality"]
  const query = () =>
    apiRequest(`api/dataQuality`, "GET", undefined, window?.origin)
  return useQuery(cacheKey, query)
}

export function useCompanyDataQuality(id?: string) {
  const cacheKey = ["dataQuality", id]
  const query = () =>
    apiRequest(
      `api/dataQuality/company/${id}`,
      "GET",
      undefined,
      window?.origin
    )
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useDailyValidationStatistics(date: string) {
  const cacheKey = ["dailyValidationStatistics", date]
  const paramString = qs.stringify({
    date,
  })
  const query = () =>
    apiRequest(`companies/daily_validation_statistics?${paramString}`)
  return useQuery(cacheKey, query)
}

export function useGroupDetails({ groupId }: { groupId?: string }) {
  const cacheKey = ["groupDetails", groupId]
  const query = () => apiRequest(`group/${groupId}`)
  return useQuery(cacheKey, query, { enabled: !!groupId })
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation(
    ({
      email,
      groupName,
      firstName,
      lastName,
      contractEndDate,
      createUserAuthToken,
      planFeatures,
      competitorLimit,
      stripeCustomerId,
      domain = null,
      arr,
    }: {
      email: string
      groupName: string
      firstName?: string
      lastName?: string
      contractEndDate?: string
      createUserAuthToken?: string
      planFeatures?: { [key: string]: boolean }
      competitorLimit?: number
      stripeCustomerId?: string
      domain?: string | null
      arr?: number
    }) => {
      return apiRequest(`group/create_new_customer`, "POST", {
        users: [
          {
            email,
            firstName,
            lastName,
            userType: "owner",
          },
        ],
        group_name: groupName,
        stripe_customer_id: stripeCustomerId,
        plan_features: planFeatures,
        contract_end_date: contractEndDate,
        competitor_limit: competitorLimit,
        purchased_companies: [],
        auth_token: createUserAuthToken,
        domain: domain,
        arr: arr,
      })
    },
    {
      onSuccess: () => {
        toast.success("User and group has been created")
        queryClient.refetchQueries(["listGroups"])
      },
      onError: (error: any) => {
        console.error(error)
        if (error?.message?.includes("UsernameExistsException")) {
          toast.error("A user with the provided email already exists")
        } else {
          toast.error("Hmm, there was an issue creating that user")
        }
      },
    }
  )
}

export function useCreateDemoCustomer() {
  return useMutation(
    ({
      users,
      groupName,
    }: {
      users: { email: string; userType: string }[]
      groupName: string
    }) => {
      return apiRequest(`group/create_new_customer`, "POST", {
        users,
        purchased_companies: [],
        group_name: groupName,
        stripe_customer_id: "",
        plan_id: "demo",
        plan_features: {},
        // hardcoded token we use on the backend to verify the
        auth_token: "NMBqTFLWTj3svitxqNgSg3EO95krlLge2SxzZRpi",
      })
    },
    {
      onSuccess: () => {
        toast.success("A new demo customer has been created")
      },
      onError: (error: any) => {
        console.error(error)
        if (error?.message?.includes("UsernameExistsException")) {
          toast.error("A user with the provided email already exists")
        } else {
          toast.error("Hmm, there was an issue creating a new demo customer")
        }
      },
    }
  )
}

export function useDisableGroup() {
  return useMutation(
    ({ groupId }: { groupId: string }) => {
      return apiRequest(`group/offboard`, "POST", {
        group_id: groupId,
      })
    },
    {
      onSuccess: () => {
        toast.success("This group was successfully disabled")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue disabling that group")
      },
    }
  )
}

export function useListGroups() {
  const cacheKey = ["listGroups"]
  const query = () => apiRequest(`group/list`)
  return useQuery(cacheKey, query)
}

export function useListPendingCompanies() {
  const cacheKey = ["listPendingCompanies"]
  const query = () => apiRequest(`group/list_pending_companies`)
  return useQuery(cacheKey, query, {
    meta: {
      disableCache: true,
    },
  })
}

export function useVerifyPendingCompany() {
  return useMutation(
    ({
      companyId,
      groupId,
      verified,
    }: {
      companyId: string
      groupId: string
      verified: boolean
    }) => {
      return apiRequest(`group/verify_pending_company`, "POST", {
        company_id: companyId,
        group_id: groupId,
        verified,
      })
    },
    {
      onSuccess: () => {
        toast.success("This company has been succesfully verified")
      },
      onError: error => {
        console.error(error)
        toast.error(
          `Hmm, there was an issue, check the browser console for more details.`
        )
      },
    }
  )
}
